import { APP_INITIALIZER, Provider, Type, inject } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { AnnouncementService } from '@sb/library/announcement';
import { AnnouncementKind, Base, BaseService, DeviceTypeManager, DialogManager, DrawerManager, Environment, FormBase, InitializeStorageKeys, OnlyStorageKeys, RouterManager, Storage } from '@sb/library/base';
import { CookieService } from 'ngx-cookie-service';
import { Observable, filter, of, tap } from 'rxjs';
import { ApiRequestManager } from './api-request.manager';
import { ApiRoute } from './api-route';
import { Authentication } from './authentication/authentication';

// Base for per-portal bootstrap services. Register in a module's providers via AppBootstrapService.Provide().
export abstract class BootstrapService extends BaseService {
    abstract Bootstrap(): Observable<any>;
    abstract readonly storageKeys: Record<string, OnlyStorageKeys<any>> | null;

    private readonly ApiRequestManager = inject(ApiRequestManager);
    private readonly AnnouncementService = inject(AnnouncementService);
    private readonly DialogManager = inject(DialogManager);
    private readonly DrawerManager = inject(DrawerManager);
    protected readonly Environment = inject(Environment);
    protected readonly RouterManager = inject(RouterManager);
    protected readonly Router = inject(Router);

    private _window = window as any;

    constructor() {
        super();
        Base._DrawerManager = this.DrawerManager;
        Base._DialogManager = this.DialogManager;
        Base._RouterManager = this.RouterManager;
        Base._DeviceTypeManager = inject(DeviceTypeManager);
        Authentication['_CookieService'] = inject(CookieService);
        Authentication['_Environment'] = this.Environment;

        ApiRoute.SetApiRequestManager(this.ApiRequestManager);
        Authentication.SetFromCookieIfAvailable();
    }

    Initialize() {
        if (this.storageKeys) {
            let storageKeyPrefix = `${this.Environment.Authentication.Portal}_`;
            if (Authentication.isAuthenticated) {
                storageKeyPrefix += `${Authentication.ProfileID}_`;
            }

            InitializeStorageKeys(storageKeyPrefix, this.storageKeys);
            Storage.ClearExpireOnRefreshStorageValues(this.storageKeys);
        }

        window.addEventListener('online', _ => {
            this.ApiRequestManager.disabled = false;
            this.AnnouncementService.ClearAnnouncement(AnnouncementKind.Offline);
        });
        window.addEventListener('offline', _ => {
            this.ApiRequestManager.disabled = true;
            this.AnnouncementService.ShowAnnouncement(AnnouncementKind.Offline);
        });

        window.onbeforeunload = () => FormBase.currentForm?.isDirty || this.DrawerManager.anyDrawerFormDirty ? '' : null;

        this.SetupGoogleAnalytics();

        this.Router.events.pipe(filter(event => event instanceof NavigationStart && this.Router.navigated)).subscribe(_ => {
            Base.BeginTransitionAnimation();
        });

        return this.Bootstrap().pipe(tap(() => {
            document.body.classList.add('app-loaded');
            this._window['schoolBoxAppLoaded'] = true;
        }));
    }

    private SetupGoogleAnalytics() {
        const analyticsID = this.Environment.Telemetry?.GoogleAnalytics?.ID;
        if (!analyticsID) {
            return;
        }

        this._window.dataLayer = this._window.dataLayer || [];
        this._window.gtag = (...args: any[]) => this._window.dataLayer.push(args);
        this._window.gtag('js', new Date());
        this._window.gtag('consent', 'default', { ad_storage: 'denied' });

        this._window.gaID = analyticsID;
        this._window.gtag('config', analyticsID, { send_page_view: false });

        const script = document.createElement('script');
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${analyticsID}`;
        document.head.appendChild(script);

        this.Router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(_ => {
            const relativeUrlPath = window.location.pathname + window.location.search;

            this._window.gtag?.('event', 'page_view', {
                page_path: relativeUrlPath,
                page_location: window.location.href,
                page_title: document.title
            });
        });
    }

    static Provide(this: Type<BootstrapService>): Provider {
        return {
            provide: APP_INITIALIZER,
            useFactory: (service: BootstrapService) => () => service.Initialize(),
            deps: [this],
            multi: true
        };
    }
}
